import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, CalendarHeart, MessageCircle, Instagram } from "lucide-react";
import { ASSETS, tel, mailto, EMERGENCY_NOTICE } from "@/data/site";
import { useContent } from "@/context/ContentContext";
import { LotusDivider } from "@/components/Botanical";

export default function Footer() {
  const CLINIC = useContent();
  return (
    <footer data-testid="site-footer" className="relative overflow-hidden bg-charcoal text-cream/80">
      <div className="container-clinic py-16 md:py-20">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-4">
              <img src={ASSETS.logo} alt="Clinic logo" className="h-16 w-16 rounded-full object-contain" />
              <div>
                <p className="font-serif text-2xl text-cream">Dr. Mithee's</p>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-rose">Clinic for Women</p>
              </div>
            </div>
            <p className="mt-6 font-serif text-xl italic text-gold">{CLINIC.tagline}</p>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-cream/60">
              Compassionate, experienced obstetric and gynaecological care for every chapter of a woman's life.
            </p>
            <div className="mt-6 flex gap-3">
              <a href={`https://wa.me/${CLINIC.whatsappDigits}`} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" data-testid="footer-whatsapp" className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-cream/15 text-cream/70 transition-colors hover:border-terracotta hover:bg-terracotta hover:text-cream"><MessageCircle className="h-5 w-5" /></a>
              <a href={CLINIC.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-cream/15 text-cream/70 transition-colors hover:border-terracotta hover:bg-terracotta hover:text-cream"><Instagram className="h-5 w-5" /></a>
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-sans text-xs font-semibold uppercase tracking-[0.25em] text-rose">Explore</h4>
            <ul className="mt-5 space-y-3 text-sm">
              {[
                { to: "/", l: "Home" },
                { to: "/about", l: "About" },
                { to: "/services", l: "Services" },
                { to: "/contact", l: "Contact" },
                { to: "/privacy", l: "Privacy Policy" },
              ].map((i) => (
                <li key={i.to}>
                  <Link to={i.to} data-testid={`footer-link-${i.l.toLowerCase().replace(/[^a-z]/g, "")}`} className="link-underline text-cream/70 hover:text-cream">
                    {i.l}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-sans text-xs font-semibold uppercase tracking-[0.25em] text-rose">Visit & Contact</h4>
            <ul className="mt-5 space-y-4 text-sm">
              <li className="flex gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <span className="text-cream/70">{CLINIC.address}</span>
              </li>
              <li className="flex gap-3">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <span className="flex flex-col text-cream/70">
                  <a href={tel(CLINIC.doctorMobile)} className="hover:text-cream">Mobile: {CLINIC.doctorMobile}</a>
                  <a href={tel(CLINIC.appointmentMobile)} className="hover:text-cream">Appointments: {CLINIC.appointmentMobile}</a>
                </span>
              </li>
              <li className="flex gap-3">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <a href={mailto(CLINIC.email)} className="break-all text-cream/70 hover:text-cream">{CLINIC.email}</a>
              </li>
            </ul>
            <Link
              to="/appointment"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-3 font-sans text-sm font-semibold text-cream transition-colors hover:bg-terracotta-dark"
            >
              <CalendarHeart className="h-4 w-4" /> Book an Appointment
            </Link>
          </div>
        </div>

        <LotusDivider className="mt-14 opacity-70" />

        <p className="mx-auto mt-8 max-w-3xl text-center text-xs leading-relaxed text-cream/45">
          {EMERGENCY_NOTICE}
        </p>

        <div className="mt-8 flex flex-col items-center justify-between gap-3 border-t border-cream/10 pt-6 text-xs text-cream/50 md:flex-row">
          <p>© {new Date().getFullYear()} Dr. Mithee's Clinic for Women. All rights reserved.</p>
          <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-cream/80">Admin</Link>
        </div>
      </div>
    </footer>
  );
}
